import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { MetaHead } from '@/components/common/MetaHead'
import { getLanguagePath } from '@/lib/utils'
import { getQuizPages } from '@/content/quiz'
import { handleCheckout } from '@/lib/checkout'
import { useAuth } from '@/lib/auth-context'
import { QuizLayout } from '@/components/quiz/QuizLayout'
import type { QuizPage } from '@/content/quiz/types'

const QUIZ_PLAN_ID = 'annual'

export default function Quiz() {
  const { i18n } = useTranslation('quiz-ui')
  const { t } = useTranslation('quiz-ui')
  const navigate = useNavigate()
  const { user } = useAuth()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [answers, setAnswers] = useState<Record<string, string | string[] | number>>({})
  const [pages, setPages] = useState<QuizPage[]>([])

  useEffect(() => {
    const lang = (i18n.language === 'zh-TW' ? 'zh-TW' : 'en') as 'en' | 'zh-TW'
    setPages(getQuizPages(lang))
  }, [i18n.language])

  const currentPage = pages[currentIndex]
  const isLastPage = currentIndex === pages.length - 1

  const handleNext = (value?: string | string[] | number) => {
    if (!currentPage) return

    const answerToStore = value ?? answers[currentPage.id]
    if (answerToStore !== undefined) {
      setAnswers(prev => ({ ...prev, [currentPage.id]: answerToStore }))
    }

    if (isLastPage) {
      const checkoutMode = import.meta.env.VITE_CHECKOUT_MODE || 'login_required'
      const allowLoginOptional = checkoutMode === 'login_optional'
      handleCheckout({
        planId: QUIZ_PLAN_ID,
        userId: user?.id,
        navigate,
        allowLoginOptional,
        searchParams: '?from=quiz',
      })
    } else {
      setCurrentIndex(prev => prev + 1)
    }
  }

  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1)
    }
  }

  if (pages.length === 0) {
    return null
  }

  return (
    <>
      <MetaHead
        title={t('meta.title', 'Quiz | Clarymind')}
        description={t('meta.description', 'Take our quiz to find the right plan for you')}
      />
      <QuizLayout
        pages={pages}
        currentIndex={currentIndex}
        answers={answers}
        onAnswerChange={(pageId, value) => setAnswers(prev => ({ ...prev, [pageId]: value }))}
        onNext={handleNext}
        onBack={handleBack}
      />
    </>
  )
}
